"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@workspace/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui/components/form";
import { Input } from "@workspace/ui/components/input";
import { signIn } from "next-auth/react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Icons } from "@/components/icons";
import { getNextAuthSignInErrorMessage } from "@/lib/auth/auth-error-codes";
import { getClientPostAuthDestination } from "@/lib/auth/callback-url";
import { setPreferredSignInProvider } from "@/lib/auth/preferred-sign-in-provider";

const QaLoginSchema = z.object({
  email: z.email(),
  secret: z.string().min(1, "Secret is required"),
});

type QaLoginValues = z.infer<typeof QaLoginSchema>;

type QaLoginFormProps = {
  showLastUsed?: boolean;
};

export function QaLoginForm({ showLastUsed = false }: QaLoginFormProps) {
  const [loading, setLoading] = useState(false);

  const form = useForm<QaLoginValues>({
    mode: "onSubmit",
    resolver: zodResolver(QaLoginSchema),
    defaultValues: {
      email: "",
      secret: "",
    },
  });

  const onSubmit = useCallback(async (data: QaLoginValues) => {
    const postAuthDestination = getClientPostAuthDestination();

    setLoading(true);

    try {
      setPreferredSignInProvider("qa-login");
      const res = await signIn("qa-login", {
        email: data.email.toLowerCase(),
        secret: data.secret,
        redirectTo: postAuthDestination,
        redirect: false,
      });

      if (res?.error || res?.url?.includes("error")) {
        toast.error("QA login failed", {
          description:
            getNextAuthSignInErrorMessage(res?.error) ??
            "Could not sign in with QA credentials.",
        });
        return;
      }

      window.location.assign(res?.url ?? postAuthDestination);
    } catch (_error) {
      toast.error("QA login failed", {
        description: "Could not sign in with QA credentials.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div className="relative">
      {showLastUsed ? (
        <div className="pointer-events-none absolute top-0 right-0">
          <span className="text-[10px] text-muted-foreground">Last used</span>
        </div>
      ) : null}
      <Form {...form}>
        <form className="space-y-3" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="employer1@vacwork.app" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="secret"
            render={({ field }) => (
              <FormItem>
                <FormLabel>QA secret</FormLabel>
                <FormControl>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button className="w-full" disabled={loading} type="submit">
            {loading ? (
              <Icons.spinner className="size-4 animate-spin" />
            ) : (
              "Sign in with QA secret"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
